/**
 * Domain types for the unsorteding model.
 *
 * These are the contracts every surface agrees on: the API persists them, the
 * SPA renders them, and the Android plugin produces evidence that resolves
 * into them. Nothing here knows about SQL or HTTP.
 *
 * Ids are plain numbers because they are database auto-increment keys. A value
 * of `null` means "not persisted yet" - the pure core mints objects before the
 * database assigns them a key, so it cannot invent one.
 */
// ---------------------------------------------------------------------------
// Copies
// ---------------------------------------------------------------------------
export const FINISHES = ['nonfoil', 'foil', 'etched', 'glossy'];
export const CONDITIONS = [
    'mint',
    'near_mint',
    'excellent',
    'good',
    'light_played',
    'played',
    'poor',
];
/**
 * Where a physical card currently is.
 *
 *  collection - shelved in a bin (the only status a picklist can pull from)
 *  in_deck    - sleeved into a deck; the deck is its location
 *  incoming   - scanned but not yet shelved (the offline queue lands here)
 *  sold/lost  - gone from storage, kept for history
 *
 * The database enforces the same rules with `ck_copies_location`; keep the two
 * in step.
 */
export const COPY_STATUSES = ['collection', 'in_deck', 'incoming', 'sold', 'lost'];
export const COPY_SOURCES = ['scan', 'csv', 'manual', 'device'];
// ---------------------------------------------------------------------------
// Physical storage
// ---------------------------------------------------------------------------
export const CONTAINER_KINDS = ['box', 'binder', 'drawer', 'case'];
// ---------------------------------------------------------------------------
// Intake
// ---------------------------------------------------------------------------
/**
 * How a scan batch picks its target bin.
 *
 *  fixed_bin     - everything into the bin the user chose
 *  auto_next_bin - first bin on the walk with room left
 *  ask_per_card  - the caller supplies a bin per scan
 */
export const ASSIGNMENT_MODES = ['fixed_bin', 'auto_next_bin', 'ask_per_card'];
// ---------------------------------------------------------------------------
// Decks and picklists
// ---------------------------------------------------------------------------
export const DECK_BOARDS = ['main', 'side', 'commander', 'companion', 'maybeboard'];
/**
 * Picklist strategy.
 *
 *  min_bins    - bundle the pull into as few bins as possible (default)
 *  slot_order  - walk strictly in address order
 *  oldest_first - consume the oldest acquisitions first
 */
export const PICKLIST_STRATEGIES = ['min_bins', 'slot_order', 'oldest_first'];
// ---------------------------------------------------------------------------
// Recognition modes (declared here so IntakeSession can reference one)
// ---------------------------------------------------------------------------
/**
 * Recognition modes. See ./recognition.ts for the evidence contracts.
 *
 *  auto   - cascade: classify frame, read the footer, fall back to art
 *  fast   - footer OCR only; low confidence goes to the review queue
 *  visual - art matching first, for pre-1998, borderless and foreign cards
 *  manual - typeahead only
 */
export const RECOGNITION_MODES = ['auto', 'fast', 'visual', 'manual'];
export const DEFAULT_RECOGNITION_MODE = 'auto';
export const DEFAULT_ASSIGNMENT_MODE = 'fixed_bin';
// ---------------------------------------------------------------------------
// Guards - used for request validation at the API edge
// ---------------------------------------------------------------------------
const includes = (values, value) => typeof value === 'string' && values.includes(value);
export const isFinish = (value) => includes(FINISHES, value);
export const isCondition = (value) => includes(CONDITIONS, value);
export const isCopyStatus = (value) => includes(COPY_STATUSES, value);
export const isCopySource = (value) => includes(COPY_SOURCES, value);
export const isContainerKind = (value) => includes(CONTAINER_KINDS, value);
export const isAssignmentMode = (value) => includes(ASSIGNMENT_MODES, value);
export const isRecognitionMode = (value) => includes(RECOGNITION_MODES, value);
export const isDeckBoard = (value) => includes(DECK_BOARDS, value);
export const isPicklistStrategy = (value) => includes(PICKLIST_STRATEGIES, value);
//# sourceMappingURL=types.js.map